// ============================================================================
// PITCH DECK RENDERER — renderPitchHtml.js
// ============================================================================
// Pure JavaScript, no browser APIs and no Supabase. Two steps:
//
//   assemblePitchRender({ deck, type, data, imageUrls })
//       Resolves a deck row against the comms content into a render model:
//       the pages' worth of text, facts and image URLs, in order.
//
//   buildPitchHtml(model, { scale })
//       Turns the model into one HTML document of fixed-size pages. Used by
//       the builder for its preview (scaled down) and by api/pitch-pdf.js for
//       the PDF (scale 1, one page per section page).
//
// A format is a page size plus a set of templates (docs/pitch-deck-generator-
// plan.md, section 5). Only `screen` exists: 1920 by 1080 CSS px. Adding A4
// later means a second entry in FORMATS and a second template set here.
//
// Style: the "Cereal" direction picked on 2026-09-03 from the three style
// mockups. Every screen page is composed as a two page magazine spread: two
// 960 px halves with a gutter, warm paper, EB Garamond for text and display,
// widely tracked Jost labels, one framed picture per half with an italic
// caption, and generous 120 px margins.
// ============================================================================

import { FORMATS, SECTION_BY_KEY, readDeckChapters, readDeckSections } from './pitchSections';
import { koppenLabel, siteTypeLabel } from '../utils/siteClassification';

export function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Paragraphs from stored text: blank lines split, single newlines join.
export function paragraphs(text) {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .split(/\n\s*\n/)
    .map(p => p.replace(/\s*\n\s*/g, ' ').trim())
    .filter(Boolean);
}

// Cut long text at a sentence end near the limit, so a 4000 character
// website text fits one spread without a mid-sentence stop.
export function clampText(text, maxChars = 1100) {
  const t = String(text || '').trim();
  if (t.length <= maxChars) return t;
  const head = t.slice(0, maxChars);
  const cut = Math.max(head.lastIndexOf('. '), head.lastIndexOf('.\n'), head.lastIndexOf('! '), head.lastIndexOf('? '));
  return (cut > maxChars * 0.5 ? head.slice(0, cut + 1) : head.replace(/\s+\S*$/, '') + '…').trim();
}

// Team names are stored lower case; the deck prints them as names.
export function titleCase(name) {
  return String(name || '').trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const chunk = (arr, n) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };
const cap = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const pad2 = n => String(n).padStart(2, '0');

// Leadership roles in the order they are listed, matched against users.job_role.
const LEADERSHIP_ROLES = [
  { rank: 0, test: /director/i, singular: 'director', plural: 'directors' },
  { rank: 1, test: /partner/i, singular: 'partner', plural: 'partners' },
  { rank: 2, test: /associate/i, singular: 'associate', plural: 'associates' },
];
const OFFICE_NAMES = { NL: 'Rotterdam', IN: 'Mumbai' };
// public.projects.status, as a client reads it. Archived projects show nothing.
const STATUS_LABELS = { completed: 'Completed', active: 'In progress', paused: 'On hold' };
// comms.publications carries its date as text, YYYY-MM (one row as a full
// day); the integer year column is mostly empty. The year comes from the date.
export function publicationYear(pub) {
  const m = /^(\d{4})/.exec(String((pub && pub.date) || ''));
  return m ? Number(m[1]) : (pub && pub.year) || '';
}
const NUMBER_WORDS = ['no', 'a', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

function leadershipRole(jobRole) {
  return LEADERSHIP_ROLES.find(r => r.test.test(String(jobRole || ''))) || null;
}
export const isLeadershipRole = jobRole => !!leadershipRole(jobRole);

// "Two directors, a partner and three associates lead the work between
// Rotterdam and Mumbai."
export function leadershipStatement(people) {
  const counts = LEADERSHIP_ROLES.map(r => ({ r, n: people.filter(p => p.rank === r.rank).length })).filter(c => c.n > 0);
  if (!counts.length) return '';
  const parts = counts.map(c => (c.n === 1 ? `${/^[aeiou]/i.test(c.r.singular) ? 'an' : 'a'} ${c.r.singular}` : `${NUMBER_WORDS[c.n] || c.n} ${c.r.plural}`));
  const who = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
  const offices = Array.from(new Set(people.map(p => p.office).filter(Boolean)));
  const where = offices.length > 1 ? ` between ${offices.slice(0, -1).join(', ')} and ${offices[offices.length - 1]}` : offices.length === 1 ? ` in ${offices[0]}` : '';
  const verb = people.length === 1 ? 'leads' : 'lead';
  return `${cap(who)} ${verb} the work${where}.`;
}

// ── Model ────────────────────────────────────────────────────────────────────

export function assemblePitchRender({ deck, type, data, imageUrls }) {
  const urls = imageUrls && typeof imageUrls === 'object' ? imageUrls : (deck.image_urls && typeof deck.image_urls === 'object' ? deck.image_urls : {});
  const rows = data || {};
  const projectsByCode = new Map((rows.projects || []).map(p => [p.project_code, p]));
  const assetById = new Map();
  [...(rows.asset_ranks || []), ...(rows.asset_tags || []), ...(rows.theme_assets || [])].forEach(r => {
    if (!r.dropbox_file_id) return;
    const cur = assetById.get(r.dropbox_file_id) || { file_name: r.file_name || '', thumb_url: null };
    if (r.thumb_url && !cur.thumb_url) cur.thumb_url = r.thumb_url;
    assetById.set(r.dropbox_file_id, cur);
  });
  let lowRes = 0;
  const urlFor = id => {
    if (!id) return null;
    if (urls[id]) return urls[id];
    const a = assetById.get(id);
    if (a && a.thumb_url) { lowRes += 1; return a.thumb_url; }
    return null;
  };

  const awards = rows.awards || [];
  const publications = rows.publications || [];
  const forCode = (list, code) => list.filter(x => Array.isArray(x.project_codes) && x.project_codes.includes(code));

  let projectNo = 0;
  const chapters = readDeckChapters(deck.chapters).map(ch => ({
    id: ch.id,
    label: ch.label || cap(ch.category),
    category: ch.category,
    projects: ch.projects.filter(p => p.include).map(p => {
      const project = projectsByCode.get(p.project_code) || {};
      projectNo += 1;
      return {
        no: projectNo,
        code: p.project_code,
        name: project.name || p.project_code,
        headline: p.headline_override || project.headline || '',
        text: clampText(p.text_override || project.website_text || '', 760),
        city: project.city || '',
        country: project.country || '',
        year: project.year_start || '',
        yearComplete: project.year_complete || '',
        status: STATUS_LABELS[project.status] || '',
        category: project.typology || '',
        builtArea: Number(project.project_size) > 0 ? `${Number(project.project_size).toLocaleString('en-GB')} m²` : '',
        plotArea: Number(project.plot_area) > 0 ? `${Number(project.plot_area).toLocaleString('en-GB')} m²` : '',
        site: Array.isArray(project.site_types) && project.site_types.length ? project.site_types.map(siteTypeLabel).join(', ') : '',
        climate: project.climate_zone ? koppenLabel(project.climate_zone) : '',
        elevation: Number.isFinite(Number(project.elevation_m)) && project.elevation_m !== null && project.elevation_m !== '' ? `${Math.round(Number(project.elevation_m))} m` : '',
        heroUrl: urlFor(p.hero_file_id),
        images: p.image_file_ids.map(urlFor).filter(Boolean),
        awards: forCode(awards, p.project_code).slice().sort((a, b) => (b.year || 0) - (a.year || 0)),
        publications: forCode(publications, p.project_code).slice().sort((a, b) => String(b.date || b.year || '').localeCompare(String(a.date || a.year || ''))),
      };
    }),
  })).filter(ch => ch.projects.length > 0);
  const allProjects = chapters.flatMap(c => c.projects);
  const selectedCodes = new Set(allProjects.map(p => p.code));

  const practice = rows.practice || {};
  const themesById = Object.fromEntries((rows.themes || []).map(t => [t.comms_id, t]));
  const highlightsFor = themeId => (rows.theme_assets || [])
    .filter(t => t.theme_id === themeId)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map(t => ({ url: urlFor(t.dropbox_file_id) || t.thumb_url || null, caption: t.caption || '', credit: t.credit || '' }))
    .filter(h => h.url);

  // People. Leadership comes from the team's job roles (directors, partners,
  // associates); the founders' bios from comms.founders attach by name. The
  // team grid shows everyone else with a prepared portrait.
  const users = (rows.users || []).filter(u => u.status === 'active' && !u.deleted_at);
  const photoByUser = new Map((rows.user_profiles || []).map(p => [Number(p.user_id), p.profile_photo_rect]));
  const founderRows = (rows.founders || []).filter(f => !f.type || f.type === 'founder');
  const bioByName = new Map(founderRows.map(f => [String(f.name || '').trim().toLowerCase(), f]));
  const leadership = users
    .map(u => ({ u, role: leadershipRole(u.job_role) }))
    .filter(x => x.role)
    .map(({ u, role }) => {
      const name = titleCase(u.name);
      const founder = bioByName.get(name.toLowerCase());
      return {
        id: Number(u.id),
        name,
        role: cap(String(u.job_role || '').trim()),
        rank: role.rank,
        office: OFFICE_NAMES[u.office_location] || u.office_location || '',
        url: urls[`user:${u.id}`] || null,
        hasPhoto: !!photoByUser.get(Number(u.id)),
        bio: founder ? founder.bio || '' : '',
      };
    })
    .sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
  const leadershipIds = new Set(leadership.map(l => l.id));
  const founders = founderRows.map(f => {
    const match = leadership.find(l => l.name.toLowerCase() === String(f.name || '').trim().toLowerCase());
    return { name: f.name || '', role: f.role || '', bio: f.bio || '', url: (match && match.url) || f.image_url || null };
  });
  const team = users
    .filter(u => !leadershipIds.has(Number(u.id)))
    .map(u => ({ name: titleCase(u.name), role: u.job_role || '', url: urls[`user:${u.id}`] || null, hasPhoto: !!photoByUser.get(Number(u.id)) }))
    .filter(m => m.url)
    .sort((a, b) => a.name.localeCompare(b.name));

  const sections = readDeckSections(deck.sections).filter(s => s.enabled).map(s => {
    const def = SECTION_BY_KEY[s.key] || { label: s.key };
    const base = { id: s.id, key: s.key, label: def.label, settings: s.settings };
    if (s.key === 'theme') {
      const theme = s.settings.theme_id ? themesById[s.settings.theme_id] : null;
      return { ...base, theme: theme ? { label: theme.label, description: theme.description || '', highlights: highlightsFor(theme.comms_id) } : null };
    }
    if (s.key === 'awards') {
      const list = (s.settings.scope === 'all' ? awards : awards.filter(a => (a.project_codes || []).some(c => selectedCodes.has(c))))
        .slice().sort((a, b) => (b.year || 0) - (a.year || 0) || String(a.award_body).localeCompare(String(b.award_body)));
      return { ...base, items: list.map(a => ({ year: a.year || '', title: a.award_body || '', detail: [a.result, a.category].filter(Boolean).join(' · '), codes: (a.project_codes || []).filter(c => projectsByCode.has(c)).map(c => projectsByCode.get(c).name) })) };
    }
    if (s.key === 'publications') {
      const list = (s.settings.scope === 'all' ? publications : publications.filter(p => (p.project_codes || []).some(c => selectedCodes.has(c))))
        .slice().sort((a, b) => String(b.date || b.year || '').localeCompare(String(a.date || a.year || '')) || String(a.magazine || a.title).localeCompare(String(b.magazine || b.title)));
      return { ...base, items: list.map(p => ({ year: publicationYear(p), title: p.magazine || p.title || '', detail: p.magazine ? p.title || '' : '', codes: (p.project_codes || []).filter(c => projectsByCode.has(c)).map(c => projectsByCode.get(c).name) })) };
    }
    if (s.key === 'team') return { ...base, members: team };
    return base;
  });

  const coverProject = allProjects.find(p => p.heroUrl) || allProjects[0] || null;
  const format = FORMATS.find(f => f.key === deck.format) || FORMATS[0];

  // Pictures for the editorial spreads (intro, practice, index): the
  // projects' heroes and gallery images in deck order, the cover first so the
  // later spreads pick the next ones along.
  const editorialImages = [];
  const seen = new Set();
  const addEditorial = (url, p) => {
    const key = pickSrc(url, 'm');
    if (key && !seen.has(key)) { seen.add(key); editorialImages.push({ url, caption: [p.name, p.city].filter(Boolean).join(', ') }); }
  };
  allProjects.forEach(p => addEditorial(p.heroUrl, p));
  allProjects.forEach(p => p.images.forEach(u => addEditorial(u, p)));

  return {
    format,
    title: deck.title || '',
    clientName: deck.client_name || '',
    date: deck.rendered_at || new Date().toISOString(),
    intro: deck.intro_text || (type && type.intro_template) || '',
    typeLabel: type ? type.label : '',
    practice: {
      studioBio: String(practice.studio_bio || ''),
      mission: String(practice.mission || '').replace(/^["“]+|["”]+$/g, ''),
      yearFounded: practice.year_founded || '',
      officeCount: practice.office_count || '',
      contact: String(practice.pitch_contact || ''),
    },
    founders,
    leadership,
    sections,
    chapters,
    coverUrl: coverProject ? coverProject.heroUrl : null,
    coverCaption: coverProject ? [coverProject.name, coverProject.city].filter(Boolean).join(', ') : '',
    editorialImages,
    lowResCount: lowRes,
    missingHero: allProjects.filter(p => !p.heroUrl).map(p => p.code),
  };
}

// ── Spread furniture ─────────────────────────────────────────────────────────

const monthYear = iso => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

let spreadN = 0;
const e = escapeHtml;

// One picture at three sizes. `l` covers a whole 1920 px page, `m` a framed
// slot inside a half (at most 720 px wide), `s` one of the small gallery
// frames (at most 460 px). Sending the largest everywhere is what made a
// forty spread deck a 43 MB PDF and pushed Chromium's print past the
// function's time limit: the print cost tracks image bytes almost exactly.
// A deck prepared before the tiers existed carries a plain URL string, and
// every slot falls back to it.
export function pickSrc(pic, size) {
  if (!pic) return null;
  if (typeof pic === 'string') return pic;
  return pic[size] || pic.m || pic.l || pic.s || null;
}

// A screen page composed as two facing pages. `left` and `right` are the
// halves' inner markup; `bleed` fills the whole page with one picture first.
function spread(left, right, { bleed = '', cls = '' } = {}) {
  spreadN += 1;
  return `<section class="page${cls ? ` ${cls}` : ''}">
    ${bleed}
    <div class="half left">${left}</div>
    <div class="half right">${right}</div>
    <div class="gutter"></div>
  </section>`;
}
const mark = (light) => `<div class="label mark${light ? ' light' : ''}">Architecture Brio</div>`;
const folio = (light) => `<div class="folio${light ? ' light' : ''}">${spreadN}</div>`;
const caption = (text, cls = '') => (text ? `<div class="caption${cls ? ` ${cls}` : ''}">${e(text)}</div>` : '');
// A framed picture inside the half's margins, caption below the frame.
const framed = (pic, cap, { inset = '120px', bottom = '' } = {}) => {
  const url = pickSrc(pic, 'm');
  return url
    ? `<div class="frame" style="inset: ${inset}${bottom ? `; bottom: ${bottom}` : ''};"><img src="${e(url)}" alt=""></div>${cap ? `<div class="caption under">${e(cap)}</div>` : ''}`
    : `<div class="frame empty" style="inset: ${inset};"></div>`;
};
const bleedImg = pic => {
  const url = pickSrc(pic, 'l');
  return url ? `<img class="bleed" src="${e(url)}" alt=""><div class="scrim"></div>` : '<div class="bleed empty"></div>';
};
const paras = (text, cls = 'body') => paragraphs(text).map(p => `<p class="${cls}">${e(p)}</p>`).join('');
const portrait = (url, name, sub) => `<div class="person">
      ${url ? `<img class="portrait" src="${e(url)}" alt="">` : '<div class="portrait empty"><span class="label tiny">Portrait</span></div>'}
      <div class="name">${e(name)}</div>
      ${sub ? `<div class="label tiny">${e(sub)}</div>` : ''}
    </div>`;

// ── Pages ────────────────────────────────────────────────────────────────────

function pageCover(m) {
  const left = `${mark()}
    <div class="stack" style="position: absolute; left: 120px; bottom: 200px; width: 640px;">
      <div class="label">Pitch · ${e(monthYear(m.date))}${m.typeLabel ? ` · ${e(m.typeLabel)}` : ''}</div>
      <h1 class="display">${e(m.title)}</h1>
      ${m.clientName ? `<p class="sub italic muted">Prepared for ${e(m.clientName)}</p>` : ''}
    </div>`;
  return spread(left, framed(m.coverUrl, m.coverCaption));
}

function pageIntro(m) {
  const signed = m.founders.map(f => e(f.name)).filter(Boolean).join(' and ');
  const pic = m.editorialImages[1] || null;
  const left = `${mark()}
    <div class="stack" style="position: absolute; left: 120px; top: 220px; width: 640px;">
      <div class="label">Introduction</div>
      <div class="text">${paras(m.intro) || '<p class="body muted">No introduction written yet.</p>'}</div>
      ${signed ? `<p class="body signature">${signed}<br><span class="caption">Founding partners, Architecture BRIO</span></p>` : ''}
    </div>`;
  return spread(left, `${pic ? framed(pic.url, pic.caption) : ''}${folio()}`);
}

function pagePractice(m) {
  const [lead, ...rest] = paragraphs(m.practice.studioBio);
  const facts = [
    m.practice.yearFounded ? `<div><div class="label">Founded</div><div class="fact">${e(m.practice.yearFounded)}</div></div>` : '',
    m.practice.officeCount ? `<div><div class="label">Studios</div><div class="fact">${e(m.practice.officeCount)}</div></div>` : '',
  ].join('');
  const pic = m.editorialImages[2] || m.editorialImages[0] || null;
  const left = `${mark()}
    <div class="stack" style="position: absolute; left: 120px; top: 220px; width: 640px;">
      <div class="label">The practice</div>
      ${lead ? `<div class="lead">${e(lead)}</div>` : ''}
      <div class="twocol">
        <div class="text">${rest.map(p => `<p class="body">${e(p)}</p>`).join('')}</div>
        ${m.practice.mission ? `<div class="text"><p class="body italic muted">${e(m.practice.mission)}</p></div>` : ''}
      </div>
    </div>
    ${facts ? `<div class="row" style="position: absolute; left: 120px; bottom: 140px; gap: 64px;">${facts}</div>` : ''}`;
  return spread(left, `${pic ? framed(pic.url, pic.caption) : ''}${folio()}`);
}

function pagesLeadership(m) {
  const pages = [];
  const groups = chunk(m.leadership, 6);
  groups.forEach((group, i) => {
    const rows = group.map(p => `<div class="line"><span class="body">${e(p.name)}</span><span class="caption">${e([p.role, p.office].filter(Boolean).join(', '))}</span></div>`).join('');
    const left = `${mark()}
      <div class="stack" style="position: absolute; left: 120px; top: 220px; width: 640px;">
        <div class="label">Leadership</div>
        ${i === 0 ? `<div class="lead">${e(leadershipStatement(m.leadership))}</div>` : ''}
        <div class="lines">${rows}</div>
      </div>`;
    const right = `<div class="grid3" style="position: absolute; left: 120px; right: 120px; top: 160px;">${group.map(p => portrait(p.url, p.name, p.role)).join('')}</div>${folio()}`;
    pages.push(spread(left, right));
  });
  // The founders' bios, two to a spread, portraits from the same source.
  const withBio = m.founders.filter(f => f.bio);
  chunk(withBio, 2).forEach(pair => {
    const half = f => `<div class="stack" style="position: absolute; left: 120px; right: 120px; top: 160px;">
        ${f.url ? `<img class="portrait small" src="${e(f.url)}" alt="">` : ''}
        <h3 class="h3">${e(f.name)}</h3>
        <div class="label">${e(f.role)}</div>
        <div class="text">${paras(clampText(f.bio, 900))}</div>
      </div>`;
    pages.push(spread(`${mark()}${half(pair[0])}`, `${pair[1] ? half(pair[1]) : ''}${folio()}`));
  });
  if (!pages.length) pages.push(spread(`${mark()}<div class="stack" style="position: absolute; left: 120px; top: 220px; width: 640px;"><div class="label">Leadership</div><p class="body muted">No directors, partners or associates found in the team's job roles.</p></div>`, folio()));
  return pages.join('');
}

// Three pictures the Cereal way: one large on the left half, two on the right.
function galleryLayout(items, cap) {
  const [a, b, c] = items;
  const pic = (it, style, size) => `<div class="frame" style="${style}"><img src="${e(pickSrc(it.url, size))}" alt=""></div>`;
  if (items.length >= 3) {
    return {
      left: `${pic(a, 'left: 120px; top: 120px; width: 720px; height: 720px;', 'm')}<div class="caption" style="position: absolute; left: 120px; top: 864px;">${e(cap(a))}</div>`,
      right: `${pic(b, 'left: 120px; top: 120px; width: 360px; height: 460px;', 's')}<div class="caption" style="position: absolute; left: 120px; top: 600px;">${e(cap(b))}</div>
        ${pic(c, 'left: 400px; top: 640px; width: 440px; height: 300px;', 's')}<div class="caption" style="position: absolute; left: 400px; top: 960px;">${e(cap(c))}</div>`,
    };
  }
  if (items.length === 2) {
    return { left: framed(a.url, cap(a)), right: framed(b.url, cap(b)) };
  }
  return { left: `<div class="caption" style="position: absolute; left: 120px; bottom: 140px;">${e(cap(a))}</div>`, right: framed(a.url, '') };
}

function pagesTheme(m, s) {
  if (!s.theme) return '';
  const first = s.theme.highlights[0] || null;
  const left = `${mark()}
    <div class="stack" style="position: absolute; left: 120px; top: 220px; width: 640px;">
      <div class="label">Theme</div>
      <h2 class="display small">${e(s.theme.label)}</h2>
      <div class="text">${paras(s.theme.description)}</div>
    </div>`;
  const pages = [spread(left, `${first ? framed(first.url, first.caption || first.credit) : ''}${folio()}`)];
  chunk(s.theme.highlights.slice(1), 3).slice(0, 2).forEach(group => {
    const lay = galleryLayout(group, h => [h.caption, h.credit].filter(Boolean).join(' · '));
    pages.push(spread(lay.left, `${lay.right}${folio()}`));
  });
  return pages.join('');
}

function pageIndex(m) {
  const entries = m.chapters.flatMap(ch => ch.projects.map(p => ({ p, ch })));
  return chunk(entries, 9).map(group => {
    const rows = group.map(({ p, ch }) => `<div class="line"><span class="num">${pad2(p.no)}</span><span class="entry">${e(p.name)}</span><span class="caption">${e([ch.label, p.city].filter(Boolean).join(' · '))}</span></div>`).join('');
    const left = `${mark()}
      <div class="stack" style="position: absolute; left: 120px; top: 200px; width: 700px;">
        <div class="label">Selected projects</div>
        <div class="lines">${rows}</div>
      </div>`;
    const first = group[0].p;
    const right = `${first.heroUrl ? `<div class="frame" style="left: 240px; top: 140px; width: 600px; height: 400px;"><img src="${e(pickSrc(first.heroUrl, 'm'))}" alt=""></div><div class="caption" style="position: absolute; left: 240px; top: 560px;">${e([first.name, first.city].filter(Boolean).join(', '))}</div>` : ''}
      ${m.practice.mission ? `<div class="body" style="position: absolute; left: 240px; bottom: 140px; width: 480px;">${e(m.practice.mission)}</div>` : ''}${folio()}`;
    return spread(left, right);
  }).join('');
}

function pageChapter(m, ch) {
  const hero = ch.projects.find(p => p.heroUrl);
  const left = `<div class="stack" style="position: absolute; left: 120px; bottom: 120px; width: 900px;">
      <div class="label light">Chapter · ${ch.projects.length} project${ch.projects.length === 1 ? '' : 's'}</div>
      <h1 class="display light">${e(ch.label)}</h1>
    </div>`;
  return spread(left, folio(true), { bleed: bleedImg(hero ? hero.heroUrl : null), cls: 'dark' });
}

// The gallery spreads of one project: at most two, at most three pictures
// each, balanced so four pictures read as two and two rather than three and a
// lonely one.
export function gallerySplit(images) {
  const n = images.length;
  if (n <= 3) return n ? [images] : [];
  if (n === 4) return [images.slice(0, 2), images.slice(2, 4)];
  return [images.slice(0, 3), images.slice(3, Math.min(n, 6))];
}

// A project's supporting pictures, with the lead image dropped if it is also
// tagged as one: the lead image has its own slot and must not take a second.
export function supportingPictures(p) {
  const hero = pickSrc(p.heroUrl, 'm');
  const seen = new Set(hero ? [hero] : []);
  return (p.images || []).filter(u => {
    const key = pickSrc(u, 'm');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function pagesProject(m, ch, p) {
  const location = [p.city, p.country].filter(Boolean).join(', ');
  const years = p.year ? (p.yearComplete && p.yearComplete !== p.year ? `${p.year} to ${p.yearComplete}` : String(p.year)) : (p.yearComplete ? String(p.yearComplete) : '');
  const capOf = () => [p.name, p.city].filter(Boolean).join(', ');
  // Opener: everything flows down from the top, so a long text can never
  // climb into the title. The text is cut at a sentence end that fits.
  const left = `${mark()}
    <div class="stack" style="position: absolute; left: 120px; top: 200px; width: 640px;">
      <div class="label">${pad2(p.no)} · ${e(ch.label)}</div>
      <h2 class="display">${e(p.name)}</h2>
      ${p.headline ? `<p class="sub italic muted">${e(p.headline)}</p>` : ''}
      <div class="text" style="width: 560px;">${paras(clampText(p.text, 620)) || '<p class="body muted">No project text yet.</p>'}</div>
    </div>`;
  // No photograph twice in one project, and the lead image belongs to the
  // chapter divider, which bleeds it across a full page. So the opener frames
  // the first supporting picture, the galleries take up to six more, and the
  // closing page keeps the next one along for itself. The lead image comes
  // back only to fill a frame that would otherwise be empty: a project with a
  // single supporting picture, or none at all. In the second case it fills
  // both the opener and the closing page, which reads better than two holes.
  const supporting = supportingPictures(p);
  const openerImage = supporting[0] || p.heroUrl || null;
  const rest = supporting.slice(1);
  const pages = [spread(left, `${openerImage ? framed(openerImage, capOf()) : ''}${folio()}`)];
  const galleryImages = rest.slice(0, Math.min(6, Math.max(0, rest.length - 1)));
  const closingImage = rest[galleryImages.length] || p.heroUrl || null;
  gallerySplit(galleryImages.map(u => ({ url: u }))).forEach(group => {
    const lay = galleryLayout(group, capOf);
    pages.push(spread(lay.left, `${lay.right}${folio()}`));
  });
  // Closing page: the facts in small type, then awards and publications with
  // their article titles, and the last picture on the right.
  const facts = [
    ['Location', location], ['Years', years], ['Status', p.status],
    ['Built area', p.builtArea], ['Plot area', p.plotArea], ['Site', p.site], ['Climate', p.climate], ['Elevation', p.elevation],
  ].filter(([, v]) => v);
  if (p.awards.length || p.publications.length || p.status) {
    // One line per entry, and a shared budget of lines so the page never
    // overflows: what does not fit is counted, the full list is in the
    // Recognition section.
    // The column runs from 200 px to a 60 px bottom margin: the title label
    // with its gap (46), the facts at 36 px a row, a gap plus label per list
    // (54), and 16 px per footnote line for whatever is left.
    const budget = Math.floor((1080 - 200 - 60 - 46 - facts.length * 36 - (p.awards.length ? 54 : 0) - (p.publications.length ? 54 : 0)) / 16);
    const factRows = facts.map(([k, v]) => `<div class="factrow"><span class="label tiny">${k}</span><span class="small">${e(v)}</span></div>`).join('');
    const awardsShown = p.awards.slice(0, Math.max(0, budget));
    const pubsShown = p.publications.slice(0, Math.max(0, budget - awardsShown.length - 1));
    const moreAwards = p.awards.length - awardsShown.length;
    const morePubs = p.publications.length - pubsShown.length;
    const awardRows = awardsShown.map(a => `<div class="foot oneline">${e([a.year, a.award_body, a.result].filter(Boolean).join(' · '))}</div>`).join('');
    const pubRows = pubsShown.map(x => `<div class="foot oneline">${e([publicationYear(x), x.magazine || x.title].filter(Boolean).join(' · '))}${x.magazine && x.title ? `<span class="caption foot"> · ${e(x.title)}</span>` : ''}</div>`).join('')
      + (morePubs || moreAwards ? `<div class="caption foot">${[moreAwards ? `${moreAwards} more award${moreAwards === 1 ? '' : 's'}` : '', morePubs ? `${morePubs} more publication${morePubs === 1 ? '' : 's'}` : ''].filter(Boolean).join(', ')}</div>` : '');
    const closingLeft = `${mark()}
      <div class="stack" style="position: absolute; left: 120px; top: 200px; width: 700px; gap: 28px;">
        <div class="label">${pad2(p.no)} · ${e(p.name)}</div>
        <div class="factlist">${factRows}</div>
        ${awardRows ? `<div><div class="label tiny" style="margin-bottom: 8px;">Awards</div><div class="footlist">${awardRows}</div></div>` : ''}
        ${pubRows ? `<div><div class="label tiny" style="margin-bottom: 8px;">Publications</div><div class="footlist">${pubRows}</div></div>` : ''}
      </div>`;
    pages.push(spread(closingLeft, `${closingImage ? framed(closingImage, capOf()) : ''}${folio()}`));
  }
  return pages.join('');
}

// Awards and publications, one line per entry in four columns across the
// spread: year, who, the projects in italics. Article titles stay on the
// projects' own closing pages.
const LIST_ROWS = 26;
function pageList(m, s, title) {
  if (!s.items.length) return '';
  const row = it => `<div class="oneline"><span class="num">${e(it.year)}</span><span class="who">${e(it.title)}${it.codes.length ? `<span class="caption small"> · ${e(it.codes.join(', '))}</span>` : ''}</span></div>`;
  const column = items => `<div class="listcol">${items.map(row).join('')}</div>`;
  return chunk(s.items, LIST_ROWS * 4).map((group, i) => {
    // Up to 52 entries: one wide column per half, balanced. Beyond: two per half.
    const perHalf = group.length > LIST_ROWS * 2 ? 2 : 1;
    const colCount = group.length <= 10 ? 1 : perHalf * 2;
    const rows = Math.ceil(group.length / colCount);
    const cols = Array.from({ length: colCount }, (_, c) => group.slice(c * rows, (c + 1) * rows));
    const half = list => `<div class="listgrid" style="grid-template-columns: repeat(${list.length}, minmax(0, 1fr));">${list.map(column).join('')}</div>`;
    const left = `${mark()}
      <div class="label" style="position: absolute; left: 120px; top: 140px;">${e(title)}${i > 0 ? ' · continued' : ''}</div>
      <div style="position: absolute; left: 120px; right: 100px; top: 200px;">${half(cols.slice(0, perHalf))}</div>`;
    const right = `<div style="position: absolute; left: 100px; right: 120px; top: 200px;">${half(cols.slice(perHalf))}</div>${folio()}`;
    return spread(left, right);
  }).join('');
}

function pageTeam(m, s) {
  if (!s.members.length) return '';
  return chunk(s.members, 16).map((group, i) => {
    const grid = list => `<div class="grid4" style="position: absolute; left: 120px; right: 120px; top: 200px;">${list.map(t => portrait(t.url, t.name, t.role)).join('')}</div>`;
    const left = `${mark()}<div class="label" style="position: absolute; left: 120px; top: 140px;">The team${i > 0 ? ' · continued' : ''}</div>${grid(group.slice(0, 8))}`;
    return spread(left, `${grid(group.slice(8))}${folio()}`);
  }).join('');
}

function pageClosing(m) {
  // The contact block keeps its line breaks: one block per office, split on
  // blank lines, the address lines inside it as written.
  const contact = String(m.practice.contact || '').replace(/\r\n/g, '\n').split(/\n\s*\n/).map(b => b.trim()).filter(Boolean);
  const left = `<div class="centre"><div class="quote">${e(m.practice.mission || 'Thank you')}</div></div>`;
  const right = `${mark()}
    <div class="twocol" style="position: absolute; left: 120px; bottom: 140px; width: 720px;">
      ${contact.length ? contact.map(p => `<div class="text"><p class="body" style="white-space: pre-line;">${e(p)}</p></div>`).join('') : '<div class="text"><p class="body muted">Rotterdam · Mumbai</p></div>'}
      <div><div class="label">Shared in confidence</div><div class="caption">Not for distribution beyond the addressee.</div></div>
    </div>`;
  return spread(left, right);
}

// ── Document ─────────────────────────────────────────────────────────────────

const css = (f, scale) => `
@import url('https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Jost:wght@400;500&display=swap');
@page { size: ${f.width}px ${f.height}px; margin: 0; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #E9E6DF; }
body { font-family: 'EB Garamond', Garamond, 'Times New Roman', serif; color: #2A2926; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.deck { transform-origin: top left; transform: scale(${scale}); width: ${f.width}px; }
.page { position: relative; width: ${f.width}px; height: ${f.height}px; overflow: hidden; background: #F6F4EF; display: flex; flex-direction: row; page-break-after: always; break-after: page; ${scale < 1 ? 'margin-bottom: 24px;' : ''} }
.page:last-child { page-break-after: auto; break-after: auto; }
.page.dark { background: #2A2926; }
.half { position: relative; width: ${f.width / 2}px; height: ${f.height}px; flex: none; }
.gutter { position: absolute; left: ${f.width / 2 - 40}px; top: 0; width: 80px; height: ${f.height}px; pointer-events: none;
  background: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.05) 50%, rgba(0,0,0,0) 100%); }
.gutter::after { content: ''; position: absolute; left: 39px; top: 0; width: 2px; height: 100%; background: linear-gradient(90deg, rgba(0,0,0,0.14), rgba(0,0,0,0.04)); }
.label { font-family: Jost, Futura, 'Century Gothic', sans-serif; font-size: 12px; letter-spacing: .32em; text-transform: uppercase; color: #8C8880; font-weight: 500; }
.label.tiny { font-size: 10px; letter-spacing: .2em; }
.label.light, .light .label { color: rgba(246,244,239,.8); }
.mark { position: absolute; top: 64px; left: 120px; }
.folio { position: absolute; bottom: 56px; right: 120px; font-family: Jost, Futura, sans-serif; font-size: 11px; letter-spacing: .2em; color: #8C8880; }
.folio.light { color: rgba(246,244,239,.7); }
.display { font-weight: 400; font-size: 72px; line-height: 1.1; margin: 0; letter-spacing: -.005em; }
h1.display { font-size: 76px; }
.display.small { font-size: 56px; line-height: 1.15; }
.display.light { color: #F6F4EF; font-size: 84px; line-height: 1.05; }
.h3 { font-weight: 400; font-size: 30px; margin: 0; }
.lead { font-size: 44px; line-height: 1.25; }
.sub { font-size: 26px; line-height: 1.4; margin: 0; }
.italic { font-style: italic; }
.muted { color: #8C8880; }
.light { color: #F6F4EF; }
.body { font-size: 22px; line-height: 1.6; margin: 0; }
.text .body + .body { margin-top: 18px; }
.caption { font-style: italic; font-size: 17px; color: #8C8880; line-height: 1.5; }
.caption.under { position: absolute; left: 120px; bottom: 80px; }
.signature { margin-top: 40px; font-weight: 500; }
.stack { display: flex; flex-direction: column; gap: 28px; }
.stack.tight { gap: 10px; }
.row { display: flex; flex-direction: row; }
.twocol { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 40px; }
.fact { font-size: 28px; margin-top: 10px; }
.lines { display: flex; flex-direction: column; }
.line { display: flex; flex-direction: row; align-items: baseline; gap: 36px; padding: 15px 0; border-top: 1px solid #D9D5CC; }
.line .num { font-size: 18px; color: #8C8880; min-width: 44px; }
.line .entry { font-size: 30px; flex-grow: 1; }
.line .entry.small { font-size: 22px; line-height: 1.45; }
.line .body { flex-grow: 1; }
.line .caption { text-align: right; }
.line .entry.small .caption { text-align: left; }
.num { font-size: 18px; color: #8C8880; }
.frame { position: absolute; overflow: hidden; background: #E9E6DF; }
.frame img { width: 100%; height: 100%; object-fit: cover; display: block; }
.frame.empty { background: #E9E6DF; }
.bleed { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }
.bleed.empty { background: #33473F; }
.scrim { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(20,18,14,0) 55%, rgba(20,18,14,.55) 100%); }
.grid3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 48px 32px; }
.grid4 { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 36px 24px; }
.person { display: flex; flex-direction: column; gap: 10px; }
.portrait { width: 100%; aspect-ratio: 4 / 5; object-fit: cover; object-position: 50% 25%; display: block; background: #E9E6DF; }
.portrait.empty { border: 1px solid #D9D5CC; display: flex; align-items: flex-end; padding: 12px; }
.portrait.small { width: 240px; aspect-ratio: 4 / 5; }
.person .name { font-size: 19px; }
.small { font-size: 16px; line-height: 1.45; }
.smalllist { display: flex; flex-direction: column; gap: 3px; }
.small.oneline { display: block; padding: 0; border: none; text-overflow: ellipsis; }
.foot, .caption.foot, .foot .caption { font-size: 10px; line-height: 16px; }
.foot.oneline { display: block; padding: 0; border: none; font-size: 10px; line-height: 16px; text-overflow: ellipsis; }
.footlist { display: flex; flex-direction: column; gap: 1px; }
.factlist { display: flex; flex-direction: column; }
.factrow { display: flex; flex-direction: row; align-items: baseline; gap: 24px; padding: 6px 0; border-top: 1px solid #D9D5CC; }
.factrow .label { width: 140px; flex: none; }
.listgrid { display: grid; gap: 0 32px; }
.listcol { display: flex; flex-direction: column; }
.oneline { display: flex; flex-direction: row; align-items: baseline; gap: 14px; padding: 6px 0; border-top: 1px solid #E3E0D9; font-size: 16px; line-height: 1.4; white-space: nowrap; overflow: hidden; }
.oneline .num { font-size: 14px; min-width: 38px; }
.oneline .who { overflow: hidden; text-overflow: ellipsis; }
.centre { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; padding: 0 160px; }
.quote { font-size: 46px; line-height: 1.3; font-style: italic; text-align: center; }
`;

export function buildPitchHtml(model, { scale = 1 } = {}) {
  spreadN = 0;
  const f = model.format || FORMATS[0];
  const pages = [];
  model.sections.forEach(s => {
    switch (s.key) {
      case 'cover': pages.push(pageCover(model)); break;
      case 'intro': pages.push(pageIntro(model)); break;
      case 'practice': pages.push(pagePractice(model)); break;
      case 'founders': pages.push(pagesLeadership(model)); break;
      case 'theme': pages.push(pagesTheme(model, s)); break;
      case 'index': if (model.chapters.length) pages.push(pageIndex(model)); break;
      case 'projects':
        model.chapters.forEach(ch => {
          if (model.chapters.length > 1) pages.push(pageChapter(model, ch));
          ch.projects.forEach(p => pages.push(pagesProject(model, ch, p)));
        });
        break;
      case 'awards': pages.push(pageList(model, s, 'Awards')); break;
      case 'publications': pages.push(pageList(model, s, 'Publications')); break;
      case 'team': pages.push(pageTeam(model, s)); break;
      case 'closing': pages.push(pageClosing(model)); break;
      default: break;
    }
  });
  const body = pages.join('\n');
  const pageCount = (body.match(/<section class="page/g) || []).length;
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(model.title)}</title>
<style>${css(f, scale)}</style>
</head>
<body><div class="deck">${body}</div></body>
</html>`;
  return { html, pageCount };
}
